import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from "../hooks/useSupabaseAuth"

const SubmissionSuccessV2: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { /* showInfo */ } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const { user, client } = useAuth();
    
    // Get data from navigation state or auth context
    const { clientId, bookingUrl, clientName: stateName, clientEmail: stateEmail } = location.state || {};
    const clientName = stateName || client?.first_name || user?.name || 'there';
    const clientEmail = stateEmail || client?.email || user?.email;

    useEffect(() => {
        // No need to clear session data as we're using BetterAuth cookies
        // The server manages session state
    }, []);

    const handleBookNow = () => {
        setIsLoading(true);
        if (bookingUrl) {
            // Navigate to booking page with pre-filled client info
            navigate('/book', { 
                state: { 
                    clientId: clientId || client?.id,
                    clientEmail: clientEmail,
                    fromIntake: true 
                } 
            });
        } else {
            navigate('/book');
        }
    };

    const handlePortalAccess = () => {
        navigate('/client/portal');
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                {/* Success Message */}
                <div className="bg-white shadow-xl rounded-2xl p-8 sm:p-12 text-center mb-8">
                    <div className="mb-6">
                        <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                        Thank You, {clientName}!
                    </h1>
                    
                    <p className="text-lg text-gray-600 mb-6">
                        Your health assessment has been successfully submitted. 
                        Hailey will review your information and prepare for your consultation.
                    </p>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                        <h3 className="text-xl font-semibold text-blue-900 mb-2">
                            What Happens Next?
                        </h3>
                        <ol className="text-left text-blue-800 space-y-2 max-w-md mx-auto">
                            <li className="flex items-start">
                                <span className="font-bold mr-2">1.</span>
                                <span>Hailey will review your assessment within 24 hours</span>
                            </li>
                            <li className="flex items-start">
                                <span className="font-bold mr-2">2.</span>
                                <span>You'll receive an email when she's ready</span>
                            </li>
                            <li className="flex items-start">
                                <span className="font-bold mr-2">3.</span>
                                <span>Book your consultation at a time that works for you</span>
                            </li>
                        </ol>
                    </div>
                </div>

                {/* Action Cards */}
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Book Consultation Card */}
                    <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                        <div className="mb-4">
                            <svg className="mx-auto h-12 w-12 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold mb-3">Ready to Book?</h2>
                        <p className="text-gray-600 mb-4">
                            Schedule your initial consultation now while your assessment is fresh
                        </p>
                        <button
                            onClick={handleBookNow}
                            disabled={isLoading}
                            className="w-full bg-primary text-white font-semibold py-3 px-6 rounded-lg 
                                     hover:bg-primary-dark transition-colors duration-300 shadow-md
                                     disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Loading...' : 'Book Your Consultation'}
                        </button>
                        <p className="text-sm text-gray-500 mt-3">
                            Most popular: 60-minute initial consultation
                        </p>
                    </div>

                    {/* Client Portal Card */}
                    <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                        <div className="mb-4">
                            <svg className="mx-auto h-12 w-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold mb-3">Your Client Portal</h2>
                        <p className="text-gray-600 mb-4">
                            Access your health assessment, messages, and resources anytime
                        </p>
                        <button
                            onClick={handlePortalAccess}
                            className="w-full bg-green-600 text-white font-semibold py-3 px-6 rounded-lg 
                                     hover:bg-green-700 transition-colors duration-300 shadow-md"
                        >
                            Access Your Portal
                        </button>
                        <p className="text-sm text-gray-500 mt-3">
                            Secure access to all your health information
                        </p>
                    </div>
                </div>

                {/* Additional Info */}
                <div className="mt-8 bg-gray-100 rounded-lg p-6 text-center">
                    <h3 className="font-semibold text-gray-900 mb-2">Need Help?</h3>
                    <p className="text-gray-600 mb-4">
                        If you have any questions or need to make changes to your assessment, 
                        please don't hesitate to reach out.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <a 
                            href={`mailto:hailey@healingwithhailey.ca?subject=Question about my health assessment`}
                            className="text-primary hover:text-primary-dark font-medium"
                        >
                            Email Hailey
                        </a>
                        <span className="hidden sm:inline text-gray-400">•</span>
                        <Link 
                            to="/contact" 
                            className="text-primary hover:text-primary-dark font-medium"
                        >
                            Contact Page
                        </Link>
                    </div>
                </div>

                {/* Session Info (for returning users) */}
                {clientEmail && (
                    <p className="text-center text-sm text-gray-500 mt-6">
                        {user?.email ? `Logged in as ${user.email}` : clientEmail ? `Session for ${clientEmail}` : null}
                    </p>
                )}
            </div>
        </div>
    );
};

export default SubmissionSuccessV2;